import { useState, useEffect, useCallback } from 'react';
import { useAdmin } from '@/hooks/useAdmin';
import { Play, Loader2, XCircle, CheckCircle2, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TranslateJobs() {
  const { getAccessTokenSilently } = useAdmin();
  const [jobs, setJobs] = useState([]);
  const [counts, setCounts] = useState({ drafts: 0, published: 0, all_missing: 0 });
  const [loading, setLoading] = useState(true);
  const [activeJob, setActiveJob] = useState(null);
  const [scope, setScope] = useState('drafts');

  const load = useCallback(async () => {
    const token = await getAccessTokenSilently();
    const [jr, cr] = await Promise.all([
      fetch(`${API}/admin/translate/jobs`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()),
      fetch(`${API}/admin/translate/scope-counts`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()),
    ]);
    setJobs(jr.jobs || []);
    setCounts(cr || {});
    const running = (jr.jobs || []).find(j => j.status === 'running' || j.status === 'queued');
    if (running) setActiveJob(running);
    setLoading(false);
  }, [getAccessTokenSilently]);

  useEffect(() => { load(); }, [load]);

  // Poll while there's an active job
  useEffect(() => {
    if (!activeJob || !['running', 'queued'].includes(activeJob.status)) return;
    const int = setInterval(async () => {
      try {
        const token = await getAccessTokenSilently();
        const r = await fetch(`${API}/admin/translate/jobs/${activeJob.id}`, { headers: { Authorization: `Bearer ${token}` } });
        if (r.ok) {
          const { job } = await r.json();
          setActiveJob(job);
          if (['done', 'failed', 'canceled'].includes(job.status)) {
            toast.success(`Job ${job.status}: ${job.completed} completed, ${job.failed} failed`);
            load();
          }
        }
      } catch {/* ignore */}
    }, 2000);
    return () => clearInterval(int);
  }, [activeJob, getAccessTokenSilently, load]);

  async function start() {
    if (!window.confirm(`Pokrenuti masovno prevođenje? Scope: ${scope} (~${counts[scope]} članaka)`)) return;
    const token = await getAccessTokenSilently();
    const r = await fetch(`${API}/admin/translate/batch`, {
      method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ scope }),
    });
    if (r.ok) { const { job } = await r.json(); setActiveJob(job); toast.success('Job pokrenut'); load(); }
    else toast.error('Greška');
  }

  async function cancel(id) {
    const token = await getAccessTokenSilently();
    await fetch(`${API}/admin/translate/jobs/${id}/cancel`, {
      method: 'POST', headers: { Authorization: `Bearer ${token}` }
    });
    toast.info('Cancel poslan');
  }

  if (loading) return <div className="p-10 flex items-center justify-center"><Loader2 className="animate-spin text-zinc-500" /></div>;

  return (
    <div className="p-6 space-y-6" data-testid="translate-jobs-page">
      <div>
        <h1 className="text-2xl font-bold text-white">Masovno prevođenje</h1>
        <p className="text-sm text-zinc-500">Prevodi članke na 10 nonHR jezika (en, de, es, fr, pt, zh, ar, hi, ru, ja)</p>
      </div>

      {/* Launcher */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5" data-testid="translate-launcher">
        <div className="flex items-center gap-3 mb-4">
          <select value={scope} onChange={e => setScope(e.target.value)} className="px-3 py-2 bg-zinc-800 border border-zinc-700 rounded text-sm text-white" data-testid="translate-scope-select">
            <option value="drafts">Drafts + scheduled ({counts.drafts})</option>
            <option value="published">Objavljeni ({counts.published})</option>
            <option value="all_missing">Svi članci (missing) ({counts.all_missing})</option>
          </select>
          <button onClick={start} disabled={!!activeJob && ['running', 'queued'].includes(activeJob?.status)}
                  className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-bold rounded disabled:opacity-50" data-testid="translate-start-btn">
            <Play size={14} /> Pokreni
          </button>
        </div>

        {/* Progress */}
        {activeJob && ['running', 'queued'].includes(activeJob.status) && (
          <div className="mt-4 p-4 bg-zinc-950 border border-zinc-700 rounded-lg" data-testid="translate-progress">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-white font-medium">
                {activeJob.status === 'queued' ? 'Čeka se pokretanje...' :
                 `Preveo ${activeJob.completed}/${activeJob.total} članaka • Greške: ${activeJob.failed}`}
              </span>
              <button onClick={() => cancel(activeJob.id)} className="text-xs text-red-400 hover:text-red-300 flex items-center gap-1">
                <XCircle size={12} /> Otkaži
              </button>
            </div>
            <div className="w-full h-2 bg-zinc-800 rounded overflow-hidden">
              <div className="h-full bg-red-500 transition-all"
                   style={{ width: `${activeJob.total ? (activeJob.completed / activeJob.total) * 100 : 0}%` }} />
            </div>
          </div>
        )}
      </div>

      {/* History */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
        <table className="w-full text-sm" data-testid="translate-jobs-table">
          <thead className="bg-zinc-950 text-zinc-400 text-xs">
            <tr>
              <th className="px-4 py-3 text-left">Status</th>
              <th className="px-4 py-3 text-left">Scope</th>
              <th className="px-4 py-3 text-right">Progres</th>
              <th className="px-4 py-3 text-left">Pokrenut</th>
              <th className="px-4 py-3 text-left">Od</th>
            </tr>
          </thead>
          <tbody>
            {jobs.map(j => (
              <tr key={j.id} className="border-t border-zinc-800 text-zinc-300">
                <td className="px-4 py-2">
                  {j.status === 'done' && <span className="text-emerald-400 flex items-center gap-1"><CheckCircle2 size={12} /> done</span>}
                  {j.status === 'running' && <span className="text-blue-400 flex items-center gap-1"><RotateCcw size={12} className="animate-spin" /> running</span>}
                  {j.status === 'failed' && <span className="text-red-400">failed</span>}
                  {j.status === 'queued' && <span className="text-amber-400">queued</span>}
                  {j.status === 'canceled' && <span className="text-zinc-500">canceled</span>}
                </td>
                <td className="px-4 py-2">{j.scope}</td>
                <td className="px-4 py-2 text-right">{j.completed}/{j.total} <span className="text-zinc-500">({j.failed} err)</span></td>
                <td className="px-4 py-2 text-xs text-zinc-500">{j.started_at ? new Date(j.started_at).toLocaleString('hr') : '—'}</td>
                <td className="px-4 py-2 text-xs text-zinc-500">{j.created_by}</td>
              </tr>
            ))}
            {jobs.length === 0 && <tr><td colSpan={5} className="text-center py-8 text-zinc-500">Nema još jobova.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
